import { chainId, chain } from "../lib/wallet";
import { useI18n } from "../i18n";
import { cn } from "./cn";
import { TOOLBAR_SCROLL, revealFocused } from "./toolbar-scroll";

export interface CoordinateHUDProps {
  hoverWorld?: { x: number; y: number } | null;
  zoomLevel?: number | null;
  /** WO-VRD-9 — "strip" = compact single-row aerospace readout for the top toolbar. */
  variant?: "panel" | "strip";
  /** WO-VRD-10 — extra node appended at the END of the strip (after Chain). */
  trailing?: React.ReactNode;
  /** WO-VRD-31 — node injected right BEFORE the X·Y coordinate readout. */
  leading?: React.ReactNode;
}

/**
 * CoordinateHUD — precision instrument (WO#5).
 *
 * Compact obsidian panel with mono data readouts. Amber ticks for
 * labels give quiet identity. Values are truthful; `—` when absent.
 */
export function CoordinateHUD({ hoverWorld, zoomLevel, variant = "panel", trailing, leading }: CoordinateHUDProps) {
  const { t } = useI18n();
  const isTestnet = chainId !== 8453;
  const zoomLabel =
    zoomLevel != null && zoomLevel > 0 ? `${zoomLevel.toFixed(2)}×` : "—";
  const xyLabel = hoverWorld
    ? `${Math.floor(hoverWorld.x).toString().padStart(3, "0")} · ${Math.floor(
        hoverWorld.y,
      )
        .toString()
        .padStart(3, "0")}`
    : "— · —";
  const chainName = chain?.name ?? String(chainId);

  if (variant === "strip") {
    return (
      <div
        data-testid="coordinate-hud"
        data-shell-variant="wo-vrd-strip"
        data-toolbar-scroll
        onFocus={revealFocused}
        className={cn(
          "inline-flex items-center h-8 px-3 gap-3 rounded-pill",
          "max-lg:gap-2 max-lg:px-2.5",
          "neon-glass",
          "font-mono text-[10px] tracking-wider select-none whitespace-nowrap",
          TOOLBAR_SCROLL,
        )}
        role="status"
        aria-live="off"
        aria-label={t("grid.hud.aria")}
      >
        <span className="strip-dot" aria-hidden />
        <StripItem label="Instrument" hideLabel>
          <span className="text-neon-teal font-display font-semibold text-[9px] tracking-[0.18em] uppercase">
            {t("grid.hud.instrument")}
          </span>
        </StripItem>
        <StripDivider />
        {leading && (
          <>
            <span className="inline-flex items-center">{leading}</span>
            <StripDivider />
          </>
        )}
        <StripItem label={t("grid.hud.xy")} testid="hud-xy">
          <span className="text-text-primary tabular-nums">{xyLabel}</span>
        </StripItem>
        <StripDivider />
        <StripItem label={t("grid.hud.zoom")} testid="hud-zoom">
          <span className="text-text-primary tabular-nums">{zoomLabel}</span>
        </StripItem>
        <StripDivider className="hidden xl:block" />
        <StripItem label={t("grid.hud.grid")} testid="hud-dimensions" className="hidden xl:inline-flex">
          <span className="text-text-secondary">1000×1000</span>
        </StripItem>
        <StripDivider />
        <StripItem label={t("grid.hud.chain")} testid="hud-chain">
          <span
            className={cn(
              "inline-flex items-center gap-1.5",
              isTestnet ? "text-state-warning" : "text-state-success",
            )}
          >
            <span
              aria-hidden
              className={cn(
                "h-1 w-1 rounded-pill",
                isTestnet ? "bg-state-warning" : "bg-state-success",
              )}
            />
            {chainName}
          </span>
        </StripItem>
        {trailing && (
          <>
            <StripDivider />
            <span className="inline-flex items-center">{trailing}</span>
          </>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid="coordinate-hud"
      data-shell-variant="wo-p1"
      className={cn(
        "relative inline-flex flex-col",
        "min-w-[210px]",
        "panel-chamfer panel-chamfer--sm",
        "font-mono text-[10px] tracking-wider",
        "select-none overflow-visible",
      )}
      role="status"
      aria-live="off"
      aria-label="Grid coordinate HUD"
    >
      {/* aerospace corner-ticks (decorative, opt-in) */}
      <span className="corner-ticks" aria-hidden>
        <span className="ct-a" />
        <span className="ct-b" />
      </span>
      <div className="chamfer-strip">
        <span className="strip-dot" aria-hidden />
        <span>{t("grid.hud.instrument")}</span>
      </div>
      <div className="p-3 space-y-1.5">
        <HudRow label={t("grid.hud.xy")} testid="hud-xy">
          <span className="text-text-primary tabular-nums">{xyLabel}</span>
        </HudRow>
        <HudRow label={t("grid.hud.zoom")} testid="hud-zoom">
          <span className="text-text-primary tabular-nums">{zoomLabel}</span>
        </HudRow>
        <HudRow label={t("grid.hud.grid")} testid="hud-dimensions">
          <span className="text-text-secondary">1000 × 1000</span>
        </HudRow>
        <HudRow label={t("grid.hud.chain")} testid="hud-chain">
          <span
            className={cn(
              "inline-flex items-center gap-1.5",
              isTestnet ? "text-state-warning" : "text-state-success",
            )}
          >
            <span
              aria-hidden
              className={cn(
                "h-1 w-1 rounded-pill",
                isTestnet ? "bg-state-warning" : "bg-state-success",
              )}
            />
            {chainName}
          </span>
        </HudRow>
      </div>
    </div>
  );
}

function StripDivider({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn("h-3.5 w-px bg-border-divider shrink-0", className)}
    />
  );
}

function StripItem({
  label,
  testid,
  hideLabel,
  className,
  children,
}: {
  label: string;
  testid?: string;
  hideLabel?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <span
      className={cn("inline-flex items-center gap-1.5", className)}
      data-testid={testid}
    >
      {!hideLabel && (
        <span className="text-text-faint uppercase text-[9px] tracking-[0.16em]">
          {label}
        </span>
      )}
      {children}
    </span>
  );
}

function HudRow({
  label,
  testid,
  children,
}: {
  label: string;
  testid: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3" data-testid={testid}>
      <span className="text-text-faint uppercase text-[9.5px] tracking-[0.18em]">
        {label}
      </span>
      {children}
    </div>
  );
}
